import React from 'react'
import { UserService, PartnerService } from '@duck/api-definitions'
import { TranslationKey } from '@/modules/i18n'
import { CohortPersonDetails } from '@/components/PartnerCohorts/cohortPageData'
import type { ColumnDef } from './generalTable.utils'

export type ColumnKey = keyof CohortPersonDetails

export type FilterType = 'text' | 'enum'

export interface EnumFilterOption {
  value: string
  label: TranslationKey
  component?: React.ReactNode
}

export interface CohortColumnDef extends ColumnDef {
  key: ColumnKey
  label: TranslationKey
  isAvailableInView: (view: 'all' | 'task') => boolean
  filterType?: FilterType
  enumOptions?: EnumFilterOption[]
}

export const allColumns: CohortColumnDef[] = [
  { key: 'personId', label: 'common.name', isAvailableInView: () => true, filterType: 'text' },
  { key: 'email', label: 'common.email', isAvailableInView: () => true, filterType: 'text' },
  {
    key: 'onboardingStatus',
    label: 'common.onboardingStatus',
    isAvailableInView: () => true,
    filterType: 'enum',
  },
  { key: 'createdAt', label: 'common.added', isAvailableInView: (view) => view === 'all', filterType: 'text' },
  {
    key: 'assignmentStatus',
    label: 'common.assignmentStatus',
    isAvailableInView: () => true,
    filterType: 'enum',
  },
  { key: 'grade', label: 'common.grade', isAvailableInView: (view) => view === 'task', filterType: 'text' },
  { key: 'feedback', label: 'common.feedback', isAvailableInView: (view) => view === 'task', filterType: 'text' },
  { key: 'startDate', label: 'common.startDate', isAvailableInView: (view) => view === 'task', filterType: 'text' },
  { key: 'submitDate', label: 'common.submitDate', isAvailableInView: (view) => view === 'task', filterType: 'text' },
]

// Status formatting and sorting helpers
const onboardingStatusBadgeColorMap: Record<UserService.PersonOnboardingStatusEnum, string> = {
  [UserService.PersonOnboardingStatusEnum.Added]: 'bg-secondary/10 text-secondary border border-secondary/20',
  [UserService.PersonOnboardingStatusEnum.Invited]: 'bg-warning/10 text-warning border border-warning/20',
  [UserService.PersonOnboardingStatusEnum.Joined]: 'bg-success/10 text-success border border-success/20',
}

export const getOnboardingStatusBadgeColor = (status: UserService.PersonOnboardingStatusEnum): string => {
  return onboardingStatusBadgeColorMap[status] || 'bg-muted/10 text-muted-foreground border border-border'
}

export const formatOnboardingStatus = (status: UserService.PersonOnboardingStatusEnum, hasInvitationToken: boolean): TranslationKey => {
  const statusMap: Record<UserService.PersonOnboardingStatusEnum, TranslationKey> = {
    [UserService.PersonOnboardingStatusEnum.Added]: 'common.added',
    [UserService.PersonOnboardingStatusEnum.Invited]: hasInvitationToken ? 'common.invitationCreated' : 'common.invited',
    [UserService.PersonOnboardingStatusEnum.Joined]: 'common.joined',
  }
  return statusMap[status] || 'common.unknown'
}

const assignmentStatusBadgeColorMap: Record<PartnerService.AssignmentDetailsStatus, string> = {
  [PartnerService.AssignmentDetailsStatus.Sent]: 'bg-secondary/10 text-primary border border-secondary/60',
  [PartnerService.AssignmentDetailsStatus.Started]: 'bg-warning/10 text-warning border border-warning/20',
  [PartnerService.AssignmentDetailsStatus.Submitted]: 'bg-success/10 text-success border border-success/20',
  [PartnerService.AssignmentDetailsStatus.Forfeited]: 'bg-destructive/10 text-destructive border border-destructive/20',
}

export const getAssignmentStatusBadgeColor = (status?: PartnerService.AssignmentDetailsStatus): string => {
  if (!status) {
    return 'bg-muted/10 text-muted-foreground border border-border'
  }
  return assignmentStatusBadgeColorMap[status] || 'bg-muted/10 text-muted-foreground border border-border'
}


export const formatAssignmentStatus = (hasInvitationToken: boolean, status?: PartnerService.AssignmentDetailsStatus): TranslationKey => {
  if (!status) {
    return 'common.notSent'
  }
  const assignmentStatusLabelMap: Record<PartnerService.AssignmentDetailsStatus, TranslationKey> = {
    [PartnerService.AssignmentDetailsStatus.Sent]: hasInvitationToken ? 'common.active' : 'common.sent',
    [PartnerService.AssignmentDetailsStatus.Started]: 'common.started',
    [PartnerService.AssignmentDetailsStatus.Submitted]: 'common.submitted',
    [PartnerService.AssignmentDetailsStatus.Forfeited]: 'common.forfeited',
  }
  return assignmentStatusLabelMap[status] || 'common.BLANK'
}

// Custom sort order for assignment status: Not Sent (undefined) -> Sent -> Started -> Submitted / Forfeited
const assignmentStatusSortOrder: Record<PartnerService.AssignmentDetailsStatus | 'notSent', number> = {
  'notSent': 0,
  [PartnerService.AssignmentDetailsStatus.Sent]: 1,
  [PartnerService.AssignmentDetailsStatus.Started]: 2,
  [PartnerService.AssignmentDetailsStatus.Submitted]: 3,
  [PartnerService.AssignmentDetailsStatus.Forfeited]: 4,
}

export const getAssignmentStatusSortValue = (status?: PartnerService.AssignmentDetailsStatus): number => {
  if (!status) {
    return assignmentStatusSortOrder['notSent']
  }
  return assignmentStatusSortOrder[status] ?? 99
}

/**
 * Generate enum options for onboarding status filter dropdown
 */
export const getOnboardingStatusOptions = (t: (key: TranslationKey) => string): EnumFilterOption[] => [
  {
    value: UserService.PersonOnboardingStatusEnum.Added,
    label: 'common.added',
    component: (
      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getOnboardingStatusBadgeColor(UserService.PersonOnboardingStatusEnum.Added)}`}>
        {t('common.added')}
      </span>
    ),
  },
  {
    value: UserService.PersonOnboardingStatusEnum.Invited,
    label: 'common.invited',
    component: (
      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getOnboardingStatusBadgeColor(UserService.PersonOnboardingStatusEnum.Invited)}`}>
        {t('common.invited')}
      </span>
    ),
  },
  {
    value: UserService.PersonOnboardingStatusEnum.Joined,
    label: 'common.joined',
    component: (
      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getOnboardingStatusBadgeColor(UserService.PersonOnboardingStatusEnum.Joined)}`}>
        {t('common.joined')}
      </span>
    ),
  },
]

/**
 * Generate enum options for assignment status filter dropdown
 */
export const getAssignmentStatusOptions = (t: (key: TranslationKey) => string): EnumFilterOption[] => [
  {
    value: 'notSent',
    label: 'common.notSent',
    component: (
      <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-muted/10 text-muted-foreground border border-border">
        {t('common.notSent')}
      </span>
    ),
  },
  {
    value: PartnerService.AssignmentDetailsStatus.Sent,
    label: 'common.sent',
    component: (
      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${assignmentStatusBadgeColorMap[PartnerService.AssignmentDetailsStatus.Sent]}`}>
        {t('common.sent/active')}
      </span>
    ),
  },
  {
    value: PartnerService.AssignmentDetailsStatus.Started,
    label: 'common.started',
    component: (
      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${assignmentStatusBadgeColorMap[PartnerService.AssignmentDetailsStatus.Started]}`}>
        {t('common.started')}
      </span>
    ),
  },
  {
    value: PartnerService.AssignmentDetailsStatus.Submitted,
    label: 'common.submitted',
    component: (
      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${assignmentStatusBadgeColorMap[PartnerService.AssignmentDetailsStatus.Submitted]}`}>
        {t('common.submitted')}
      </span>
    ),
  },
  {
    value: PartnerService.AssignmentDetailsStatus.Forfeited,
    label: 'common.forfeited',
    component: (
      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${assignmentStatusBadgeColorMap[PartnerService.AssignmentDetailsStatus.Forfeited]}`}>
        {t('common.forfeited')}
      </span>
    ),
  },
]

/**
 * Get enum options for a specific column
 */
export const getEnumOptionsForColumn = (
  columnKey: ColumnKey,
  t: (key: TranslationKey) => string
): EnumFilterOption[] | undefined => {
  if (columnKey === 'onboardingStatus') return getOnboardingStatusOptions(t)
  if (columnKey === 'assignmentStatus') return getAssignmentStatusOptions(t)
  return undefined
}

/**
 * Custom sorting logic for CohortPeopleTable
 */
export const sortCohortPeopleDetails = (
  peopleDetails: CohortPersonDetails[],
  persons: UserService.Person[],
  sortColumn: string | null,
  sortDirection: 'asc' | 'desc' | null
): CohortPersonDetails[] => {
  if (!sortColumn || !sortDirection) {
    return peopleDetails
  }

  return [...peopleDetails].sort((a, b) => {
    let aValue: string | number | boolean | undefined | null
    let bValue: string | number | boolean | undefined | null

    let comparison = 0

    // Special handling for personId - sort by person name
    if (sortColumn === 'personId') {
      const personA = persons.find(p => p.id === a.personId)
      const personB = persons.find(p => p.id === b.personId)
      const nameA = `${personA?.firstName || ''} ${personA?.lastName || ''}`.trim()
      const nameB = `${personB?.firstName || ''} ${personB?.lastName || ''}`.trim()
      comparison = (nameA || a.personId).localeCompare(nameB || b.personId)
    }
    // Special handling for assignmentStatus - custom sort order
    else if (sortColumn === 'assignmentStatus') {
      const aSortValue = getAssignmentStatusSortValue(a.assignmentStatus)
      const bSortValue = getAssignmentStatusSortValue(b.assignmentStatus)
      comparison = aSortValue - bSortValue
    }
    // Handle date strings (ISO format)
    else {
      const columnKey = sortColumn as keyof CohortPersonDetails
      aValue = a[columnKey] as string | number | boolean | undefined | null
      bValue = b[columnKey] as string | number | boolean | undefined | null

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1
      if (bValue === null || bValue === undefined) return -1

      const dateColumns = ['createdAt', 'startDate', 'submitDate']
      if (dateColumns.includes(sortColumn) && typeof aValue === 'string' && typeof bValue === 'string') {
        const dateA = new Date(aValue).getTime()
        const dateB = new Date(bValue).getTime()
        comparison = dateA - dateB
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue)
      } else if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue
      } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        comparison = aValue === bValue ? 0 : aValue ? 1 : -1
      } else {
        comparison = String(aValue).localeCompare(String(bValue))
      }
    }

    return sortDirection === 'asc' ? comparison : -comparison
  })
}

/**
 * Custom filtering logic for CohortPeopleTable
 */
export const filterCohortPeopleDetails = (
  peopleDetails: CohortPersonDetails[],
  persons: UserService.Person[],
  availableColumns: CohortColumnDef[],
  getColumnFilter: (columnKey: string) => string,
  getColumnFilterArray: (columnKey: string) => string[]
): CohortPersonDetails[] => {
  let filtered = peopleDetails

  // Apply column filters
  availableColumns.forEach(col => {
    if (col.filterType === 'enum') {
      const filterValues = getColumnFilterArray(col.key)
      if (filterValues.length === 0) return

      const filterSet = new Set(filterValues)
      filtered = filtered.filter(item => {
        const columnKey = col.key as keyof CohortPersonDetails
        const value: string | undefined | null = item[columnKey] as string | undefined | null

        // Special handling for assignmentStatus - "notSent" means undefined
        if (columnKey === 'assignmentStatus') {
          const statusValue = value || 'notSent'
          return filterSet.has(statusValue)
        }

        // For other enum columns
        if (value === null || value === undefined) return false
        return filterSet.has(value)
      })
    } else {
      const filterValue = getColumnFilter(col.key)
      if (!filterValue.trim()) return

      const searchTerm = filterValue.toLowerCase()
      filtered = filtered.filter(item => {
        const columnKey = col.key as keyof CohortPersonDetails
        const value = item[columnKey]

        // Special handling for personId - filter by person name
        if (columnKey === 'personId') {
          const person = persons.find(p => p.id === item.personId)
          const fullName = `${person?.firstName || ''} ${person?.lastName || ''}`.trim()
          return fullName.toLowerCase().includes(searchTerm)
        }

        // Convert value to string for filtering
        if (value === null || value === undefined) return false
        return String(value).toLowerCase().includes(searchTerm)
      })
    }
  })

  return filtered
}
